/**
 * RunwayBar
 *
 * Two-layer horizontal bar visualising content runway:
 *   Solid layer    - scheduled days (locked on calendar, committed)
 *   Faded layer    - library-approved days (approved but not yet scheduled; potential)
 *
 * Visual language mirrors PipelineBar: same track height ratio (h-2), same
 * rounded-full approach, same compact caption below.
 *
 * Target marker at 14 days (configurable) shows the "healthy runway" goal.
 * Overflow (total > targetDays) shown as a tiny "+" badge at the end.
 *
 * Empty state: full-width dashed track + "Empty" caption in coral.
 * Nothing-planned state: planned=0 but ready>0 → "Nothing planned" label in coral below bar.
 *
 * Wrapped in GlossaryTooltip (metric-runway) - no hover flicker since the
 * tooltip is on the outer container and the bar itself has no nested hover
 * interactions.
 */

import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GlossaryTooltip } from './GlossaryTooltip';

export interface RunwayBarProps {
  scheduledDays: number;
  /** Pre-computed days of runway from library (approved but unscheduled) */
  libraryDays: number;
  /** Visual target marker position in days - default 14 */
  targetDays?: number;
  /** Compact row variant */
  compact?: boolean;
  className?: string;
  /** Optional inline content rendered at end of caption row (e.g., velocity arrow) */
  trailingSlot?: ReactNode;
  /**
   * When true, skips the GlossaryTooltip wrapper and renders the bar
   * directly. Use in contexts (e.g. drawers) where the parent already
   * provides a tooltip and the inline-flex wrapper would collapse w-full.
   */
  fullWidth?: boolean;
  /** On mobile, do not wrap in the tap-to-open glossary sheet (use inside tappable cards). */
  disableGlossaryOnMobile?: boolean;
  /** Hide the planned/ready numbers (the Low pipeline label next to the bar already says it); the trailing slot stays. */
  hideCaption?: boolean;
}

export const RunwayBar = ({
  scheduledDays,
  libraryDays,
  targetDays = 14,
  compact = false,
  className = '',
  trailingSlot,
  fullWidth = false,
  disableGlossaryOnMobile = false,
  hideCaption = false,
}: RunwayBarProps) => {
  const { t } = useTranslation('agency');

  const totalDays = scheduledDays + libraryDays;
  const isEmpty = scheduledDays === 0 && libraryDays === 0;
  const isCliff = scheduledDays === 0 && libraryDays > 0;

  // Cap visual widths at targetDays for proportional rendering
  const visualTarget = Math.max(targetDays, 1);

  const scheduledPct = Math.min((scheduledDays / visualTarget) * 100, 100);
  // Library segment fills up to the cap, but only the portion not already used by scheduled
  const libraryRaw = (libraryDays / visualTarget) * 100;
  const libraryPct = Math.min(libraryRaw, Math.max(0, 100 - scheduledPct));

  const isOverflow = totalDays > targetDays;

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (isEmpty) {
    const emptyLabel = t('runway_bar_empty', { defaultValue: 'Empty' });
    const emptyContent = (
      <div
        className={`space-y-1 ${compact ? 'w-full max-w-[180px]' : 'w-full'} ${className}`}
        role="img"
        aria-label={t('runway_bar_aria_empty', { defaultValue: 'Pipeline: empty - nothing planned or ready' })}
      >
        <div className="h-2 w-full rounded-full border border-dashed border-gray-300" />
        <p className="text-[10px] font-medium text-coral-600 leading-none tabular-nums">
          {emptyLabel}
        </p>
      </div>
    );
    return (
      <GlossaryTooltip termId="metric-runway" side="top" fullWidth={fullWidth} disableOnMobile={disableGlossaryOnMobile}>
        {emptyContent}
      </GlossaryTooltip>
    );
  }

  // ── Normal state ─────────────────────────────────────────────────────────────

  // Target marker position - capped to 100% visually
  const markerPct = Math.min((targetDays / visualTarget) * 100, 100);

  const ariaLabel = t('runway_bar_aria_label', {
    defaultValue:
      'Pipeline: {{scheduled}} days planned, {{library}} days ready. Target: {{target}} days.',
    scheduled: scheduledDays,
    library: libraryDays,
    target: targetDays,
  });

  const normalContent = (
    <div
      className={`space-y-1 ${compact ? 'w-full max-w-[180px]' : 'w-full'} ${hideCaption ? 'min-w-[150px]' : ''} ${className}`}
      role="img"
      aria-label={ariaLabel}
    >
        {/* ── Bar ─────────────────────────────────────────────────── */}
        <div className="relative h-2 w-full">
          {/* Track */}
          <div className="absolute inset-0 rounded-full bg-gray-100" />

          {/* Scheduled segment - solid, primary color */}
          {scheduledPct > 0 && (
            <div
              className="absolute top-0 left-0 h-full bg-primary-500 rounded-l-full transition-all"
              style={{ width: `${scheduledPct}%` }}
            />
          )}

          {/* Library segment - diagonal stripe pattern (potential) */}
          {libraryPct > 0 && (
            <div
              className="absolute top-0 h-full transition-all"
              style={{
                left: `${scheduledPct}%`,
                width: `${libraryPct}%`,
                // Diagonal stripe = approved-but-unscheduled "potential" feel.
                // Resolved at render time from the whitelabel CSS variable so
                // re-themed agencies get their own brand colour, not a hardcoded teal.
                backgroundImage: `repeating-linear-gradient(45deg, var(--color-secondary-400, #26a69a) 0px, var(--color-secondary-400, #26a69a) 2px, transparent 2px, transparent 5px)`,
                opacity: 0.55,
                borderRadius:
                  scheduledPct === 0
                    ? '9999px'
                    : scheduledPct + libraryPct >= 100
                    ? '0 9999px 9999px 0'
                    : '0',
              }}
            />
          )}

          {/* Round the right end of the filled portion if it reaches the end */}
          {scheduledPct + libraryPct >= 100 && scheduledPct > 0 && libraryPct === 0 && (
            <div
              className="absolute top-0 right-0 h-full"
              style={{ width: 0 }}
            />
          )}

          {/* Target marker - vertical tick at targetDays position */}
          {!isOverflow && (
            <div
              className="absolute top-0 h-full w-px border-l border-gray-400/60"
              style={{ left: `${markerPct}%` }}
              aria-hidden="true"
            />
          )}

          {/* Overflow indicator */}
          {isOverflow && (
            <div
              className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-full pl-1"
              aria-hidden="true"
            >
              <span className="text-[9px] font-bold text-primary-500 leading-none">+</span>
            </div>
          )}
        </div>

        {/* ── Caption ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-1">
          {hideCaption ? null : scheduledDays === 0 ? (
            <span className="text-[10px] font-semibold text-coral-600 tabular-nums leading-none">
              {t('runway_bar_cliff', { defaultValue: 'Nothing planned' })}
            </span>
          ) : (
            <span className="text-[10px] text-gray-600 tabular-nums leading-none font-medium">
              <span className="text-primary-600 font-semibold">{t('runway_bar_days_short', { days: scheduledDays, defaultValue: '{{days}}d' })}</span>
              {' '}
              {t('runway_bar_label_scheduled', { defaultValue: 'planned' })}
            </span>
          )}
          {!hideCaption && libraryDays > 0 && (
            <>
              <span className="text-gray-300 text-[10px]" aria-hidden="true">·</span>
              <span className="text-[10px] text-gray-500 tabular-nums leading-none">
                {t('runway_bar_days_short', { days: libraryDays, defaultValue: '{{days}}d' })} {t('runway_bar_label_library', { defaultValue: 'ready' })}
              </span>
            </>
          )}
          {isCliff && libraryDays === 0 && (
            /* nothing extra - cliff label covers it */
            null
          )}
          {trailingSlot && <span className="ml-auto inline-flex items-center">{trailingSlot}</span>}
        </div>
      </div>
  );

  return (
    <GlossaryTooltip termId="metric-runway" side="top" fullWidth={fullWidth} disableOnMobile={disableGlossaryOnMobile}>
      {normalContent}
    </GlossaryTooltip>
  );
};
